import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Typography, Button, Alert, Checkbox } from '@mui/material';
import axiosInstance from '../utils/axiosInstance';
import TableView from '../components/TableView';
import { useSnackbar } from '../contexts/SnackbarProvider';
import { formatCurrency } from '../utils/format';
import { useSelectRow } from '../utils/useSelectRow';

const getBillingBasisLabel = (basis) => {
    switch (basis) {
        case 'ordered_at':
            return '受注日';
        case 'shipped_at':
            return '出荷日';
        case 'delivered_at':
            return '納品日';
        default:
            return '-';
    }
};

const BillingClosingSection = () => {
    const { showSnackbar } = useSnackbar();
    const [items, setItems] = useState([]);

    const {
        selectedItems,
        setSelectedItems,
        handleRowSelect,
        handleSelectAll,
    } = useSelectRow('billing_rule_id');

    const fetchPreview = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/invoice/closing-preview');
            setItems(Array.isArray(res.data.items) ? res.data.items : []);
        } catch (err) {
            showSnackbar(
                err?.response?.data?.message || '締めデータ取得に失敗しました',
                'error'
            );
        }
    }, [showSnackbar]);

    useEffect(() => {
        fetchPreview();
    }, [fetchPreview]);

    const handleCreateSelected = async () => {
        if (selectedItems.length === 0) {
            showSnackbar('選択されていません', 'warning');
            return;
        }

        const confirmed = window.confirm(
            `${selectedItems.length}件の請求を作成しますか？`
        );

        if (!confirmed) return;

        try {
            await Promise.all(
                selectedItems.map((billingRuleId) => {
                    const row = items.find(i => i.billing_rule_id === billingRuleId);
                    return axiosInstance.post('/invoice/close', {
                        client_id: row.client_id,
                        billing_rule_id: billingRuleId
                    });
                })
            );

            showSnackbar('請求を一括作成しました', 'success');
            setSelectedItems([]);
            fetchPreview();
        } catch (err) {
            showSnackbar(
                err?.response?.data?.message || '一括作成に失敗しました',
                'error'
            );
        }
    };

    const handleCreateInvoice = async (row) => {
        const confirmed = window.confirm(
            `${row.client_name} の請求書を作成しますか？`
        );

        if (!confirmed) return;

        try {
            await axiosInstance.post('/invoice/close', {
                client_id: row.client_id,
                billing_rule_id: row.billing_rule_id
            });

            showSnackbar('作成しました', 'success');
            fetchPreview();
        } catch (err) {
            showSnackbar('作成に失敗しました', 'error');
        }
    };

    const columns = [
        {
            label: (
                <Checkbox
                    checked={selectedItems.length === items.length && items.length > 0}
                    indeterminate={
                        selectedItems.length > 0 &&
                        selectedItems.length < items.length
                    }
                    onChange={(e) => handleSelectAll(e, items)}
                />
            ),
            render: (item) => (
                <Checkbox
                    checked={selectedItems.includes(item.billing_rule_id)}
                    onChange={() => handleRowSelect(item.billing_rule_id)}
                />
            ),
            width: '50px',
        },
        {
            label: '取引先',
            accessor: 'client_name',
            width: '240px',
            render: (item) => <Typography>{item.client_name}</Typography>
        },
        {
            label: 'ルール名',
            accessor: 'rule_name',
            width: '200px'
        },
        {
            label: '請求基準',
            accessor: 'billing_basis',
            width: '120px',
            render: (item) => getBillingBasisLabel(item.billing_basis)
        },
        {
            label: '注文件数',
            accessor: 'order_count',
            width: '120px',
            render: (item) => `${item.order_count}件`
        },
        {
            label: '金額',
            accessor: 'total_amount',
            width: '180px',
            render: (item) => (
                <>
                    {formatCurrency(item.total_amount)}
                    <br />
                    <Typography variant="caption">
                        税込 {formatCurrency(item.total_amount_including_tax)}
                    </Typography>
                </>
            ),
        },
        {
            label: '',
            render: (item) => (
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleCreateInvoice(item)}
                >
                    作成
                </Button>
            )
        }
    ];

    return (
        <>
            <Alert severity="info" sx={{ mb: 1 }}>
                締め請求を利用するには、<Link to="/clients">取引先画面</Link>で設定してください。
            </Alert>
            <Alert severity="info">
                本日までの未請求データのみ集計されます
            </Alert>

            <Button
                variant="contained"
                disabled={selectedItems.length === 0}
                onClick={handleCreateSelected}
                sx={{ my: 1 }}
            >
                選択した請求書をまとめて作成
            </Button>

            <TableView
                dataset={items}
                columns={columns}
                keyColumn="billing_rule_id"
            />
        </>
    );
};

export default BillingClosingSection;